/**
 * board-client — the Run Board's data layer (Story D): fetch a board page
 * (`GET /runs/<id>`) and the host status (`GET /status`) the header pill folds
 * in, plus the one run-level mutation the board fires — `cancelRun`
 * (`POST /runs/<id>/cancel`). Reuses the honest-error posture from `client.ts`;
 * the board poller (Story C's cached-polling model) drives the reads on a
 * relaxed interval.
 *
 * Auth: every endpoint here is authorized by the daemon, and the browser's
 * credential is the oauth2-proxy session cookie carried by {@link apiFetch}.
 * Cancel is no exception — the edge no longer injects the daemon's trigger
 * secret on that path, so the operator's own session is what authorizes the
 * mutation, which is the point: a cancel is now attributable to a person.
 * Same-origin through Caddy, so no CORS.
 */

import { ReadApiError, SessionError, apiFetch } from './api-fetch'
import type { HostDegradationSubsystem, HostStatus } from './board-host'
import type { HostLensSnapshot, RunBoardSnapshot } from './board-types'

/** A board page plus the snapshot ETag, so the poller can send `If-None-Match`. */
export interface BoardResult {
  /** The decoded board, or null on a `304 Not Modified` (caller keeps the last). */
  snapshot: RunBoardSnapshot | null
  etag: string | null
  /** True when the daemon answered `304` — nothing changed since `etag`. */
  notModified: boolean
}

/**
 * Fetch one page of a Run Board. Sends `If-None-Match` when an `etag` is held so
 * an unchanged board returns `304` and transfers almost nothing; on `304` the
 * snapshot is null and `notModified` is true. Throws {@link ReadApiError} on any
 * other non-2xx so the caller renders an honest error naming the endpoint.
 */
export async function fetchBoard(
  runId: string,
  options: {
    etag?: string | null
    limit?: number
    cursor?: string | null
    fetchImpl?: typeof fetch
    timeoutMs?: number
  } = {}
): Promise<BoardResult> {
  const { etag = null, limit, cursor = null, fetchImpl = fetch, timeoutMs = 10_000 } = options
  const endpoint = boardEndpoint(runId, limit, cursor)
  const headers: Record<string, string> = {}
  if (etag !== null) {
    headers['if-none-match'] = etag
  }

  const res = await apiFetch({ endpoint, headers, fetchImpl, timeoutMs })
  if (res.status === 304) {
    return { snapshot: null, etag, notModified: true }
  }
  if (!res.ok) {
    throw new ReadApiError(endpoint, res.status, `GET ${endpoint} returned ${res.status}`)
  }
  const snapshot = (await res.json()) as RunBoardSnapshot
  return { snapshot, etag: res.headers.get('etag') ?? etag, notModified: false }
}

/**
 * The daemon's `POST /runs/<id>/cancel` response body (graceful phase-1). The
 * counts report what the cancel did: queued/leased jobs cancelled outright,
 * running jobs left to drain, the run's total, and whether the run was already
 * fully cancelled (the idempotent no-op case).
 */
export interface CancelRunResult {
  runId: string
  cancelledQueued: number
  cancelledLeased: number
  runningLeftToFinish: number
  totalJobs: number
  alreadyCancelled: boolean
}

/**
 * Cancel a run gracefully via `POST /runs/<id>/cancel`. Authorized by the
 * caller's own session cookie (see the module header). The daemon endpoint is
 * idempotent, so cancelling an already-terminal run is a safe `200` no-op
 * (`alreadyCancelled: true`). Any non-2xx throws a {@link ReadApiError} naming
 * the endpoint — including a {@link SessionError} when the session died between
 * board load and click — so the caller renders an honest failure and reverts its
 * optimistic update, mirroring the read layer's posture. A `2xx` returns the
 * parsed {@link CancelRunResult}.
 */
export async function cancelRun(
  runId: string,
  options: { fetchImpl?: typeof fetch; timeoutMs?: number } = {}
): Promise<CancelRunResult> {
  const { fetchImpl = fetch, timeoutMs = 10_000 } = options
  const endpoint = `/runs/${encodeURIComponent(runId)}/cancel`

  const res = await apiFetch({ endpoint, method: 'POST', fetchImpl, timeoutMs })
  if (!res.ok) {
    throw new ReadApiError(endpoint, res.status, `POST ${endpoint} returned ${res.status}`)
  }
  return (await res.json()) as CancelRunResult
}

/** A Host lens page plus its ETag, so the poller can send `If-None-Match`. */
export interface HostLensResult {
  /** The decoded lens, or null on a `304 Not Modified` (caller keeps the last). */
  snapshot: HostLensSnapshot | null
  etag: string | null
  /** True when the daemon answered `304` — nothing changed since `etag`. */
  notModified: boolean
}

/**
 * Fetch one page of the Host lens (`GET /host-lens`) — the cross-run view. Same
 * `If-None-Match`/`304` + honest-error posture as {@link fetchBoard}; on `304`
 * the snapshot is null and `notModified` is true.
 */
export async function fetchHostLens(
  options: {
    etag?: string | null
    limit?: number
    cursor?: string | null
    fetchImpl?: typeof fetch
    timeoutMs?: number
  } = {}
): Promise<HostLensResult> {
  const { etag = null, limit, cursor = null, fetchImpl = fetch, timeoutMs = 10_000 } = options
  const endpoint = hostLensEndpoint(limit, cursor)
  const headers: Record<string, string> = {}
  if (etag !== null) {
    headers['if-none-match'] = etag
  }

  const res = await apiFetch({ endpoint, headers, fetchImpl, timeoutMs })
  if (res.status === 304) {
    return { snapshot: null, etag, notModified: true }
  }
  if (!res.ok) {
    throw new ReadApiError(endpoint, res.status, `GET ${endpoint} returned ${res.status}`)
  }
  const snapshot = (await res.json()) as HostLensSnapshot
  return { snapshot, etag: res.headers.get('etag') ?? etag, notModified: false }
}

/** Build the `GET /host-lens` URL with optional `limit`/`cursor` query params. */
function hostLensEndpoint(limit: number | undefined, cursor: string | null): string {
  const params = new URLSearchParams()
  if (limit !== undefined) {
    params.set('limit', String(limit))
  }
  if (cursor !== null) {
    params.set('cursor', cursor)
  }
  const qs = params.toString()
  return qs.length > 0 ? `/host-lens?${qs}` : '/host-lens'
}

/** Build the `GET /runs/<id>` URL with optional `limit`/`cursor` query params. */
function boardEndpoint(runId: string, limit: number | undefined, cursor: string | null): string {
  const params = new URLSearchParams()
  if (limit !== undefined) {
    params.set('limit', String(limit))
  }
  if (cursor !== null) {
    params.set('cursor', cursor)
  }
  const qs = params.toString()
  return qs.length > 0 ? `/runs/${encodeURIComponent(runId)}?${qs}` : `/runs/${encodeURIComponent(runId)}`
}

/**
 * Fetch and decode the host status the board header folds in. `GET /status` is
 * authorized like every other route (only `GET /health` is anonymous). A
 * transport or decode failure here is non-fatal to the board — the caller keeps
 * the calm pill rather than alarming — so this swallows those and returns null.
 *
 * An identity failure is NOT swallowed. Returning null for a `401`/`403` is
 * precisely the empty-but-healthy lie this release exists to remove: the pill
 * would read `host: connecting` forever and an operator would take a dead
 * session for an idle host. {@link SessionError} propagates so the App can show
 * the signed-out or not-permitted state instead.
 */
export async function fetchHostStatus(
  options: { fetchImpl?: typeof fetch; timeoutMs?: number } = {}
): Promise<HostStatus | null> {
  const { fetchImpl = fetch, timeoutMs = 10_000 } = options
  const res = await apiFetch({ endpoint: '/status', fetchImpl, timeoutMs }).catch((err: unknown) => {
    if (err instanceof SessionError) {
      throw err
    }
    return null
  })
  if (res === null) {
    return null
  }
  // 503 still carries a decodable body — a saturated/degraded host reports
  // itself, and that is exactly what the pill must show.
  if (!res.ok && res.status !== 503) {
    return null
  }
  try {
    return decodeHostStatus((await res.json()) as RuntimeStatusBody)
  } catch {
    return null
  }
}

/** The subset of the `GET /status` body the board reads (see runtime-status.ts). */
interface RuntimeStatusBody {
  state?: HostStatus['state']
  saturated?: boolean
  degradations?: Array<Record<string, unknown>>
  subsystems?: {
    scheduler?: { admitting?: boolean; activeWorkers?: number }
    host?: { freeRamMb?: number; loadAverage?: number; freeDiskPercent?: number }
    images?: { queued?: number; building?: number; ready?: number; failed?: number }
  }
}

/** Decode the rich `/status` body into the board's terse {@link HostStatus}. */
export function decodeHostStatus(body: RuntimeStatusBody): HostStatus {
  const host = body.subsystems?.host ?? {}
  const images = body.subsystems?.images
  // Preserve the subsystem alongside the rendered text: the board pill must tell
  // a host degradation (RAM/load/disk) apart from a non-host one (a routine
  // scheduler recovery-backlog) so the calm-by-default rule does not alarm on
  // ordinary bookkeeping (design spec, decision 2).
  const degradations = (body.degradations ?? []).map((d) => ({
    text: describeDegradation(d),
    subsystem: degradationSubsystem(d),
  }))
  return {
    state: body.state ?? 'starting',
    admitting: body.subsystems?.scheduler?.admitting ?? true,
    saturated: body.saturated ?? false,
    gauges: {
      freeRamMb: host.freeRamMb ?? 0,
      loadAverage: host.loadAverage ?? 0,
      freeDiskPercent: host.freeDiskPercent ?? 0,
    },
    degradations,
    activeWorkers: body.subsystems?.scheduler?.activeWorkers,
    images: images
      ? {
          queued: images.queued ?? 0,
          building: images.building ?? 0,
          ready: images.ready ?? 0,
          failed: images.failed ?? 0,
        }
      : undefined,
  }
}

/** The subsystems `/status` raises a degradation against; unknown values drop to undefined. */
function degradationSubsystem(d: Record<string, unknown>): HostDegradationSubsystem | undefined {
  return d.subsystem === 'host' || d.subsystem === 'images' || d.subsystem === 'scheduler' ? d.subsystem : undefined
}

/** Render one machine-parseable degradation into a short operator string. */
function describeDegradation(d: Record<string, unknown>): string {
  const reason = typeof d.reason === 'string' ? d.reason : 'degraded'
  switch (reason) {
    case 'free-ram':
      return 'RAM cap'
    case 'load-average':
      return 'load cap'
    case 'free-disk':
      return 'disk cap'
    case 'build-failures':
      return 'image build failures'
    case 'recovery-backlog':
      return 'recovery backlog'
    default:
      return reason
  }
}
